import { useState, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Collapse from '@mui/material/Collapse';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import AddIcon from '@mui/icons-material/Add';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import {
  useAvsjekkList,
  useCreateAvsjekk,
  defaultAvsjekkFilters,
  type AvsjekkFilters,
  type AvsjekkRecord,
  type CreateAvsjekkInput,
} from '../hooks/useAvsjekk';
import { useRole, hasMinimumRole } from '../hooks/useRole';
import { apiGet } from '../services/api';

interface IncomeLineOption {
  InntektslinjeID: string;
  Ansvar: string;
  AnsvarNavn?: string;
  Konto: string;
  KontoNavn: string;
  Enhet: string;
  Aar: number;
}

interface IncomeLineSearchResponse {
  data: IncomeLineOption[];
  pagination: { page: number; pageSize: number; totalCount: number };
}

function useIncomeLineSearch() {
  const [options, setOptions] = useState<IncomeLineOption[]>([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useMemo(() => ({ current: null as ReturnType<typeof setTimeout> | null }), []);

  const search = useCallback(
    (input: string) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (!input || input.length < 2) {
        setOptions([]);
        return;
      }
      timerRef.current = setTimeout(async () => {
        setLoading(true);
        try {
          const result = await apiGet<IncomeLineSearchResponse>(
            `/income-lines?search=${encodeURIComponent(input)}&pageSize=10&page=1`
          );
          setOptions(result.data ?? []);
        } catch {
          setOptions([]);
        } finally {
          setLoading(false);
        }
      }, 300);
    },
    [timerRef]
  );

  return { options, loading, search };
}

const currentYear = new Date().getFullYear();

const yearOptions = [
  { value: '', label: 'Alle år' },
  ...Array.from({ length: 5 }, (_, i) => ({
    value: String(currentYear - i),
    label: String(currentYear - i),
  })),
];

const monthOptions = [
  { value: '', label: 'Alle måneder' },
  { value: '1', label: 'Januar' }, { value: '2', label: 'Februar' },
  { value: '3', label: 'Mars' }, { value: '4', label: 'April' },
  { value: '5', label: 'Mai' }, { value: '6', label: 'Juni' },
  { value: '7', label: 'Juli' }, { value: '8', label: 'August' },
  { value: '9', label: 'September' }, { value: '10', label: 'Oktober' },
  { value: '11', label: 'November' }, { value: '12', label: 'Desember' },
];

const statusFilterOptions = [
  { value: '', label: 'Alle statuser' },
  { value: 'OK', label: 'OK' },
  { value: 'Avvik', label: 'Avvik' },
  { value: 'Uavklart', label: 'Uavklart' },
];

function statusChipColor(status: string): 'success' | 'error' | 'warning' | 'default' {
  if (status === 'OK') return 'success';
  if (status === 'Avvik') return 'error';
  if (status === 'Uavklart') return 'warning';
  return 'default';
}

function monthName(m: number): string {
  const names = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Des'];
  return names[m] || String(m);
}

function formatDate(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

const columns: GridColDef[] = [
  { field: 'InntektslinjeID', headerName: 'Inntektslinje', flex: 1, minWidth: 180 },
  { field: 'Ansvar', headerName: 'Ansvar', width: 80 },
  {
    field: 'Konto', headerName: 'Konto', width: 140,
    renderCell: (params) => (
      <>
        {params.value}
        {params.row.KontoNavn && (
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
            ({params.row.KontoNavn})
          </Typography>
        )}
      </>
    ),
  },
  {
    field: 'Periode_maaned', headerName: 'Periode', width: 110,
    valueGetter: (_value, row) => `${monthName(row.Periode_maaned)} ${row.Periode_aar}`,
  },
  {
    field: 'Status', headerName: 'Status', width: 100,
    renderCell: (params) => <Chip label={params.value} size="small" color={statusChipColor(params.value)} />,
  },
  {
    field: 'Kommentar', headerName: 'Kommentar', width: 200,
    renderCell: (params) => (
      <Typography variant="body2" noWrap title={params.value || ''}>{params.value || '-'}</Typography>
    ),
  },
  { field: 'Utfoert_av', headerName: 'Utført av', width: 130, valueFormatter: (value: string | null) => value || '-' },
  { field: 'Utfoert_dato', headerName: 'Dato', width: 110, valueFormatter: (value: string | null) => formatDate(value) },
];

export function AvsjekkPage() {
  const role = useRole();
  const canEdit = hasMinimumRole(role, 'Editor');
  const [filters, setFilters] = useState<AvsjekkFilters>(defaultAvsjekkFilters);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<CreateAvsjekkInput>({
    inntektslinjeID: '',
    periode_aar: currentYear,
    periode_maaned: new Date().getMonth() + 1,
    status: 'OK',
    kommentar: '',
    referanse: '',
  });
  const [formError, setFormError] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const { data, isLoading, error } = useAvsjekkList(filters);
  const createMutation = useCreateAvsjekk();
  const ilSearch = useIncomeLineSearch();

  const updateFilter = (partial: Partial<AvsjekkFilters>) =>
    setFilters((prev) => ({ ...prev, ...partial, page: partial.page ?? 1 }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!formData.inntektslinjeID.trim()) {
      setFormError('InntektslinjeID er påkrevd');
      return;
    }
    createMutation.mutate(formData, {
      onSuccess: () => {
        setShowForm(false);
        setFormData({
          inntektslinjeID: '', periode_aar: currentYear,
          periode_maaned: new Date().getMonth() + 1, status: 'OK', kommentar: '', referanse: '',
        });
        queryClient.invalidateQueries({ queryKey: ['avsjekk'] });
      },
      onError: (err) => setFormError(err instanceof Error ? err.message : 'Kunne ikke registrere avsjekk'),
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box>
          <Typography variant="h2">Avsjekk</Typography>
          <Typography variant="body2" color="text.secondary">Månedlig avsjekk per inntektslinje.</Typography>
        </Box>
        {canEdit && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Lukk skjema' : 'Ny avsjekk'}
          </Button>
        )}
      </Box>

      {/* Create form */}
      <Collapse in={showForm && canEdit}>
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Registrer avsjekk</Typography>
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
            <Autocomplete
              size="small"
              sx={{ minWidth: 340 }}
              freeSolo
              options={ilSearch.options}
              loading={ilSearch.loading}
              getOptionLabel={(opt) =>
                typeof opt === 'string' ? opt : opt.InntektslinjeID
              }
              filterOptions={(x) => x}
              inputValue={formData.inntektslinjeID}
              onInputChange={(_e, value, reason) => {
                setFormData((p) => ({ ...p, inntektslinjeID: value }));
                if (reason === 'input') ilSearch.search(value);
              }}
              onChange={(_e, value) => {
                if (value && typeof value !== 'string') {
                  setFormData((p) => ({ ...p, inntektslinjeID: value.InntektslinjeID }));
                }
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.InntektslinjeID}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {option.InntektslinjeID}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.Ansvar}{option.AnsvarNavn ? ` — ${option.AnsvarNavn}` : ''} · {option.Konto} {option.KontoNavn}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Inntektslinje"
                  placeholder="Søk på ansvar, konto, navn..."
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {ilSearch.loading ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
            />
            <FormControl size="small" sx={{ minWidth: 90 }}>
              <InputLabel id="form-aar-label">År</InputLabel>
              <Select labelId="form-aar-label" value={formData.periode_aar} label="År"
                onChange={(e) => setFormData((p) => ({ ...p, periode_aar: Number(e.target.value) }))}>
                {Array.from({ length: 5 }, (_, i) => currentYear - i).map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="form-maaned-label">Måned</InputLabel>
              <Select labelId="form-maaned-label" value={formData.periode_maaned} label="Måned"
                onChange={(e) => setFormData((p) => ({ ...p, periode_maaned: Number(e.target.value) }))}>
                {monthOptions.filter((o) => o.value !== '').map((o) => (
                  <MenuItem key={o.value} value={Number(o.value)}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel id="form-status-label">Status</InputLabel>
              <Select labelId="form-status-label" value={formData.status} label="Status"
                onChange={(e) => setFormData((p) => ({ ...p, status: e.target.value }))}>
                <MenuItem value="OK">OK</MenuItem>
                <MenuItem value="Avvik">Avvik</MenuItem>
                <MenuItem value="Uavklart">Uavklart</MenuItem>
              </Select>
            </FormControl>
            <TextField size="small" label="Kommentar" value={formData.kommentar}
              onChange={(e) => setFormData((p) => ({ ...p, kommentar: e.target.value }))} sx={{ minWidth: 200 }} />
            <TextField size="small" label="Referanse" value={formData.referanse}
              onChange={(e) => setFormData((p) => ({ ...p, referanse: e.target.value }))}
              placeholder="Fakturanr, saksnr..." sx={{ minWidth: 180 }} />
            <Button type="submit" variant="contained" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Lagrer...' : 'Lagre'}
            </Button>
          </Box>
          {formError && <Alert severity="error" sx={{ mt: 1 }}>{formError}</Alert>}
        </Paper>
      </Collapse>

      {/* Filters */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel id="filter-aar-label">År</InputLabel>
          <Select labelId="filter-aar-label" value={filters.periode_aar} label="År" onChange={(e) => updateFilter({ periode_aar: e.target.value })}>
            {yearOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel id="filter-maaned-label">Måned</InputLabel>
          <Select labelId="filter-maaned-label" value={filters.periode_maaned} label="Måned" onChange={(e) => updateFilter({ periode_maaned: e.target.value })}>
            {monthOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel id="filter-status-label">Status</InputLabel>
          <Select labelId="filter-status-label" value={filters.status} label="Status" onChange={(e) => updateFilter({ status: e.target.value })}>
            {statusFilterOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Kunne ikke laste avsjekkliste: {error instanceof Error ? error.message : 'Ukjent feil'}
        </Alert>
      )}

      <DataGrid
        rows={data?.data ?? []}
        columns={columns}
        getRowId={(row) => row.AvsjekkID}
        loading={isLoading}
        paginationMode="server"
        rowCount={data?.pagination?.totalCount ?? 0}
        paginationModel={{ page: filters.page - 1, pageSize: data?.pagination?.pageSize ?? 50 }}
        onPaginationModelChange={(model) => setFilters((prev) => ({ ...prev, page: model.page + 1 }))}
        pageSizeOptions={[25, 50, 100]}
        disableRowSelectionOnClick
        autoHeight
        sx={{ bgcolor: 'background.paper' }}
      />
    </Box>
  );
}
